import { useCallback, useState } from "react";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useNavigate } from "react-router";
import { processError } from "../../../../common/utils/base_error";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { BancoPagamento } from "../../domain/models/banco_pagamento";
import { initialBancoPagamentoFiltersValues } from "../hooks/banco_pagamento_lista/filters";
import { useBancoPagamentoListaPagination } from "../hooks/banco_pagamento_lista/pagination";
import { DropDown } from "../../../../common/domain/models/dropdown";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { useSaldoBancarioListaFilters } from "../hooks/saldo_bancario_lista/filters";

type LoadBancoPagamentoListaState = {

    idsBancoPagamento?: number[];
    status?: number | null;

    page?: number;
    rowsPerPage?: number;

}

export const useSaldoBancarioListaViewModel = () => {

    const {
        reports: {
            getReportSaldoBancarioListaUseCase,
        },
        financeiro: {
            getAllBancoPagamentoWithFiltersUseCase,

        },
        commons: {
            getBancosPagamentoDropDownUseCase,

        },

    } = useDependencyInjection();

    const navigate = useNavigate();
    const { pushErrorNotification, pushWarnNotification, pushInfoNotification, setPageLoading } = usePageLoader();
    const [reportViewerState, startReportViewerProcess] = useReportViewer();


    const debouncedRef = useCreateDebounceHandler(1500);

    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [errorMessage, setErrorMessage] = useState<string>();
    const [bancoPagamentoLista, setBancoPagamentoLista] = useState<BancoPagamento[]>();
    const [ddlBancoPagamento, setDdlBancoPagamento] = useState<DropDown[]>();

    const [selectedIdBancoPagamentoRelatorio, setSelectedIdBancoPagamentoRelatorio] = useState<number | null>(null);
    const [saldoBancarioReportModalOpen, setSaldoBancarioReportModalOpen] = useState(false);

    const filters = useSaldoBancarioListaFilters();
    const pagination = useBancoPagamentoListaPagination();



    //#region CARREGAMENTO.


    const loadBancoPagamentoLista = useCallback(async (funcState?: LoadBancoPagamentoListaState) => {

        try {

            const filteredStatus = evaluateParamValue(funcState?.status, filters.status);
            const filteredIdBancoPagamento = funcState?.idsBancoPagamento ?? filters.idsBancoPagamento;

            const filteredPage = funcState?.page ?? pagination.page;
            const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

            await getAllBancoPagamentoWithFiltersUseCase.getAllBancoPagamentoWithFilters(
                filteredIdBancoPagamento,
                filteredStatus ?? 2,
                filteredPage,
                filteredRowsPerPage
            );

            setBancoPagamentoLista(getAllBancoPagamentoWithFiltersUseCase.items);

            pagination.setRowsPerPage(getAllBancoPagamentoWithFiltersUseCase.pageSize);
            pagination.setTotalItems(getAllBancoPagamentoWithFiltersUseCase.totalItems);
        } catch (e: unknown) {
            setBancoPagamentoLista([]);
            throw new Error("Falha ao buscar bancos!");
        }
    }, [
        filters,
        pagination,
        getAllBancoPagamentoWithFiltersUseCase,
    ]);

    const loadBancoPagamentoDropDown = useCallback(async () => {
        try {
            setPageLoading(true);
            const result = await getBancosPagamentoDropDownUseCase.getBancosPagamentoDropDown();
            setDdlBancoPagamento(result);
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar banco!'));
        } finally {
            setPageLoading(false);
        }

    }, [getBancosPagamentoDropDownUseCase]);


    //#endregion



    //#region PAGE START
    usePageStartBootstrap(async () => {
        try {
            if (bancoPagamentoLista === undefined) {
                await loadBancoPagamentoLista();
                await loadBancoPagamentoDropDown();

            }
        } catch (e: unknown) {
            setErrorMessage(processError(e, "Falha ao buscar lista de Bancos!")[0]);
        }

    });

    //#endregion





    //#region PAGINAÇÃO.

    const handleSaldoBancarioEdit = useCallback((idBancoPagamento: number | null) => {
        navigate('/SaldoBancario_cadastro', { state: { idBancoPagamento } });

    }, [navigate]);

    const handleReload = useCallback(async () => {
        try {
            setLoading(true);
            await loadBancoPagamentoLista();
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [loadBancoPagamentoLista]);


    const handlePageChange = useCallback(async (page: number) => {
        try {
            setLoading(true);
            pagination.setPage(page + 1);
            await loadBancoPagamentoLista({ page: page + 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar lista de contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadBancoPagamentoLista]);


    const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
        try {
            setLoading(true);
            pagination.setPage(1);
            pagination.setRowsPerPage(rowsPerPage);
            await loadBancoPagamentoLista({ rowsPerPage, page: 1 });
        } catch (e: unknown) {
            pushErrorNotification('Error', processError(e, 'Falha ao buscar contas a receber!'));
        } finally {
            setLoading(false);
        }
    }, [pagination, loadBancoPagamentoLista]);

    //#endregion





    //#region RELATÓRIOS

    const openSaldoBancarioReportModal = useCallback((idBancoPagamento: number | null, openState: boolean) => {
        setSelectedIdBancoPagamentoRelatorio(idBancoPagamento);
        setSaldoBancarioReportModalOpen(openState);
    }, []);

    const handleSaldoBancarioReportModalSubmit = useCallback(async (formValues: any) => {
        try {
            setSaving(true);
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportSaldoBancarioListaUseCase.execute({
                        idBancoPagamento: formValues.idBancoPagamento,
                        dataDe: formValues.dataDe,
                        dataAte: formValues.dataAte,
                        tipoMovimento: formValues.tipoMovimento,
                        requiredMimeType: mimeType
                    });
                }
            });
            setSaldoBancarioReportModalOpen(false);
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de saldo bancário!'));
        } finally {
            setSaving(false);
        }
    }, [
        getReportSaldoBancarioListaUseCase,
        startReportViewerProcess,
        pushErrorNotification,
    ]);



    const handleSaldoBancarioExportar = useCallback(async (idBancoPagamento: number) => {
        try {
            setLoading(true);
            await startReportViewerProcess({
                fetchCallback: async (mimeType) => {
                    return await getReportSaldoBancarioListaUseCase.execute({ idBancoPagamento, requiredMimeType: mimeType });
                }
            });

        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao gerar relatório de bancos!'));
        } finally {
            setLoading(false);
        }

    }, [
        getReportSaldoBancarioListaUseCase,
        startReportViewerProcess,
        pushErrorNotification,
    ]);


    //#endregion    





    //#region PESQUISA.

    const handleIdsBancoPagamentoChange = useDebouncedCallback(
        debouncedRef,
        (newValues: number[]) => {
            filters.setIdsBancoPagamento(newValues)
            return async () => {
                try {
                    setLoading(true);
                    pagination.setPage(1);
                    await loadBancoPagamentoLista({ idsBancoPagamento: newValues, page: 1 });
                } catch (e: unknown) {
                    pushErrorNotification("Error", processError(e, "Falha ao buscar Bancos!"));
                } finally {
                    setLoading(false);
                }
            };
        }, [filters,
        pagination,
        loadBancoPagamentoLista,]
    );


    const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number) => {
        filters.setStatus(status)
        return async () => {
            try {
                setLoading(true);
                pagination.setPage(1);
                await loadBancoPagamentoLista({ status, page: 1 });
            } finally {
                setLoading(false);
            }
        };
    }, [loadBancoPagamentoLista]);


    const handleClearFilters = useCallback(async () => {
        try {
            setLoading(true);
            filters.clearFilters();
            pagination.setPage(1);
            await loadBancoPagamentoLista({
                ...initialBancoPagamentoFiltersValues,
                page: 1
            });
        } catch (e: unknown) {
            pushErrorNotification("Erro", processError(e, 'Falha ao buscar bancos!'));
        } finally {
            setLoading(false);
        }
    }, [filters, pagination, loadBancoPagamentoLista, pushErrorNotification]);

    //#endregion 



    return {
        loading,
        saving,
        bancoPagamentoLista: bancoPagamentoLista ?? [],
        errorMessage,
        filters,
        pagination,
        openDeleteModal,
        ddlBancoPagamento: ddlBancoPagamento ?? [],
        reportViewerState,
        saldoBancarioReportModalOpen,
        selectedIdBancoPagamentoRelatorio,

        openSaldoBancarioReportModal,
        handleSaldoBancarioReportModalSubmit,
        handleSaldoBancarioExportar,
        handleSaldoBancarioEdit,
        handleIdsBancoPagamentoChange,
        handleReload,
        handlePageChange,
        handleRowsPerPageChange,
        handleStatusChange,
        handleClearFilters

    };
}
